import React, { useState } from 'react';

const ExcessJunkClientsQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [formData, setFormData] = useState({
    action_type: 'follow_up',
    description: '',
    details: '',
    new_status: record?.status || 'pendiente'
  });
  const [loading, setLoading] = useState(false);

  const actionTypes = [
    { value: 'follow_up', label: 'Seguimiento Realizado', icon: '📞' },
    { value: 'email_sent', label: 'Email Enviado', icon: '📧' },
    { value: 'meeting_scheduled', label: 'Reunión Programada', icon: '📅' },
    { value: 'issue_resolved', label: 'Problema Resuelto', icon: '✅' },
    { value: 'escalated', label: 'Escalado', icon: '⚠️' },
    { value: 'comment_added', label: 'Comentario Agregado', icon: '💬' },
    { value: 'status_changed', label: 'Cambio de Estado', icon: '🔄' }
  ];

  const statusOptions = [
    { value: 'pendiente', label: '⏳ Pendiente' },
    { value: 'en_proceso', label: '🔄 En Proceso' },
    { value: 'completado', label: '✅ Completado' },
    { value: 'cancelado', label: '❌ Cancelado' }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const actionData = {
        ...formData,
        user_name: currentUser?.name || currentUser?.email || 'Sistema'
      };

      await onSave(record.id, actionData);
    } catch (error) {
      console.error('Error al guardar acción:', error);
      alert('Error al registrar la acción: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const selectedActionType = actionTypes.find(type => type.value === formData.action_type);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-orange-600 to-yellow-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">⚡ Acción Rápida</h2>
              <p className="text-orange-100 mt-1">
                Registrar acción para: <strong>{record?.nombre_cliente}</strong>
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-orange-100 hover:text-white transition-colors p-2 hover:bg-orange-700 rounded-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Tipo de Acción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de Acción *
            </label>
            <div className="grid grid-cols-1 gap-2">
              {actionTypes.map((type) => (
                <label
                  key={type.value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-all ${
                    formData.action_type === type.value
                      ? 'border-orange-500 bg-orange-50 text-orange-700'
                      : 'border-gray-300 hover:border-orange-300 hover:bg-orange-50'
                  }`}
                >
                  <input
                    type="radio"
                    name="action_type"
                    value={type.value}
                    checked={formData.action_type === type.value}
                    onChange={handleChange}
                    className="sr-only"
                  />
                  <span className="text-xl mr-3">{type.icon}</span>
                  <span className="font-medium">{type.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Cambio de Estado (solo si se selecciona status_changed) */}
          {formData.action_type === 'status_changed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Estado *
              </label>
              <select
                name="new_status"
                value={formData.new_status}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción de la Acción *
            </label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder={`Describe brevemente la acción realizada...`}
            />
          </div>

          {/* Detalles Adicionales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detalles Adicionales
            </label>
            <textarea
              name="details"
              value={formData.details}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              placeholder="Información adicional, próximos pasos, observaciones..."
            />
          </div>

          {/* Información del Cliente */}
          <div className="bg-gray-50 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-2">Información del Cliente</h3>
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Cliente:</span>
                <span className="ml-2 font-medium">{record?.nombre_cliente}</span>
              </div>
              <div>
                <span className="text-gray-500">Estado Actual:</span>
                <span className="ml-2 font-medium">
                  {statusOptions.find(s => s.value === record?.status)?.label || record?.status}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Célula:</span>
                <span className="ml-2 font-medium">{record?.celula || 'N/A'}</span>
              </div>
              <div>
                <span className="text-gray-500">Mes:</span>
                <span className="ml-2 font-medium">{record?.mes || 'N/A'}</span>
              </div>
            </div>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {loading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              )}
              {selectedActionType?.icon} Registrar Acción
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ExcessJunkClientsQuickActionModal;
